import React, { useState, useEffect } from "react";
import {
  Grid,
  Switch,
  Typography,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormHelperText,
  Button,
  Box,
  Paper,
  Divider,
} from "@mui/material";
import { TimePicker, MobileTimePicker } from "@mui/x-date-pickers";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useAuthStore } from "../../stores/zustand";

/**
 * Exercise Settings Form Component
 *
 * Focused component for managing exercise reminder settings
 * including time selection, frequency, exercise types, and day selection.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.settings - Exercise settings object
 * @param {Array} props.preferences - Available exercise preferences
 * @param {Function} props.onUpdateSetting - Callback for updating settings
 * @param {Function} props.onToggleDay - Callback for toggling day selection
 * @param {Function} props.onSave - Callback for saving settings
 * @param {Function} props.onSnoozeToggle - Callback for snooze toggle
 * @param {boolean} props.disabled - Whether form is disabled
 */
const ExerciseSettingsForm = ({
  settings,
  preferences = [],
  onUpdateSetting,
  onInitialize,
  onToggleDay,
  onSave,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  // Dropdown menu state for exercise types selector
  const [selectOpen, setSelectOpen] = useState(false);

  // Retrieve current product type from auth store (e.g., 'physio_user', 'corporate', 'preventive_course')
  const currentProduct = useAuthStore((state) => state.currentProduct);

  // Initialize physio settings when product is 'physio_user'
  // For physio users, enable physio mode by default and clear exercise type selection
  useEffect(() => {
    if (!currentProduct) return; // Simplified check

    if (currentProduct === "physio_user") {
      if (typeof onInitialize === "function") {
        onInitialize({
          isPhysioEnabled: true,
          selectedTypes: [],
        });
      } else {
        onUpdateSetting("isPhysioEnabled", true);
        onUpdateSetting("selectedTypes", []);
      }
    }
  }, [currentProduct, onInitialize, onUpdateSetting]);

  // Flag: True if user's product is 'physio_user' (dedicated physiotherapy practitioners)
  const isPhysioUser = currentProduct === "physio_user";
  // Flag: True if current product allows optional physio mode toggle (corporate or preventive_course users)
  const isCurrentProductPhysioSelectable = [
    "corporate",
    "preventive_course",
  ].includes(currentProduct);

  // Days of week with translation keys for reminder scheduling
  const dayOptions = [
    { value: 1, label: t("monday") },
    { value: 2, label: t("tuesday") },
    { value: 3, label: t("wednesday") },
    { value: 4, label: t("thursday") },
    { value: 5, label: t("friday") },
    { value: 6, label: t("saturday") },
    { value: 7, label: t("sunday") },
  ];

  // Interval frequency options for reminder notifications (in hours)
  // Values represent hours between reminders: 0.5h, 1h, 1.5h, 2h, 2.5h, 3h, 3.5h, 4h
  const frequencyOptions = [
    { value: "0.5", label: t("every30mins") },
    { value: "1", label: t("every60mins") },
    { value: "1.5", label: t("every90mins") },
    { value: "2", label: t("every120mins") },
    { value: "2.5", label: t("every150mins") },
    { value: "3", label: t("every180mins") },
    { value: "3.5", label: t("every210mins") },
    { value: "4", label: t("every240mins") },
  ];

  // Select appropriate time picker component based on device (mobile uses touch-friendly MobileTimePicker)
  const TimePickerComponent = isMobile ? MobileTimePicker : TimePicker;

  /**
   * Handle time field changes (startTime or endTime)
   * Formats selected time from dayjs object to HH:mm string format for API
   * @param {string} field - Field name ('startTime' or 'endTime')
   * @param {dayjs.Dayjs} newValue - Selected time from DatePicker
   */
  const handleTimeChange = (field, newValue) => {
    if (newValue) {
      const formattedTime = newValue.format("HH:mm");
      onUpdateSetting(field, formattedTime);
    }
  };

  /**
   * Handle exercise types multi-select changes
   * Supports "select all" logic: clicking "select all" toggles between all selected and none selected
   * @param {Event} event - Material-UI Select change event
   */
  const handleExerciseTypesChange = (event) => {
    const value = event.target.value;
    if (value.includes("all")) {
      // Toggle select all logic: if all are selected, clear; otherwise select all
      if (settings.selectedTypes.length === preferences.length) {
        onUpdateSetting("selectedTypes", []);
      } else {
        onUpdateSetting(
          "selectedTypes",
          preferences.map((pref) => pref.id)
        );
      }
    } else {
      onUpdateSetting("selectedTypes", value);
    }
  };

  /**
   * Handle physio mode toggle for corporate/preventive_course users
   * When toggled, clears exercise type selection (physio and regular exercises are mutually exclusive)
   * @param {Event} e - Checkbox change event
   */
  const handlePhysioToggle = (e) => {
    onUpdateSetting("isPhysioEnabled", e.target.checked);
    onUpdateSetting("selectedTypes", []);
  };

  /**
   * Format selected exercise types for display in the Select component
   * Maps preference IDs to human-readable names and joins with commas
   * Shows "loading" if preferences are still being fetched
   * @returns {string} Comma-separated list of selected exercise type names
   */
  const getSelectedTypesDisplay = () => {
    if (preferences.length === 0) {
      return t("loading");
    }

    const selectedNames = settings.selectedTypes
      .map((id) => {
        const preference = preferences.find((pref) => pref.id === id);
        return preference ? preference.name : null;
      })
      .filter(Boolean);

    return selectedNames.join(", ");
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: isMobile ? 2 : 3,
        maxWidth: "100%",
        mx: "auto",
        mb: 2, // Add margin bottom for spacing in scroll
        minHeight: isMobile ? "400px" : "500px", // Ensure minimum height for scroll detection
      }}
      className="exerciseCard"
    >

      {/* Header Controls: Enable/Disable Exercise Reminders */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {/* Toggle switch for enabling/disabling all exercise reminders */}
        <FormControlLabel
          control={
            <Switch
              className="exerciseSwitch"
              checked={settings.isEnabled}
              onChange={(e) => onUpdateSetting("isEnabled", e.target.checked)}
              disabled={disabled}
              color="primary"
            />
          }
          label={
            <Typography variant="subtitle1" fontWeight={600}>
              {settings.isEnabled
                ? t("reminderEnabled")
                : t("reminderDisabled")}
            </Typography>
          }
        />
      </Box>

      {/* Exercise Types Multi-Select (hidden if physio mode is enabled) */}
      {!settings.isPhysioEnabled && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t("exerciseTypes")} *
          </Typography>
          {/* Multi-select dropdown with "Select All" option and checkboxes for each exercise type */}
          <FormControl
            fullWidth
            size="small"
            error={settings.selectedTypes.length === 0}
          >
            <Select
              multiple
              open={selectOpen}
              onOpen={() => setSelectOpen(true)}
              onClose={() => setSelectOpen(false)}
              value={settings.selectedTypes}
              onChange={handleExerciseTypesChange}
              disabled={disabled || !settings.isEnabled}
              renderValue={() => getSelectedTypesDisplay()}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 300 },
                },
              }}
            >
              {/* "Select All" option with tri-state checkbox */}
              <MenuItem value="all">
                <Checkbox
                  checked={settings.selectedTypes.length === preferences.length}
                  indeterminate={
                    settings.selectedTypes.length > 0 &&
                    settings.selectedTypes.length < preferences.length
                  }
                />
                {t("selectAll")}
              </MenuItem>

              {/* Individual exercise type options with checkboxes */}
              {preferences.map((preference) => (
                <MenuItem key={preference.id} value={preference.id}>
                  <Checkbox
                    checked={settings.selectedTypes.includes(preference.id)}
                  />
                  {preference.name}
                </MenuItem>
              ))}

              {/* Divider and "Okay" button to close dropdown */}
              <Divider />
              <Box sx={{ textAlign: "right", p: 1 }}>
                <Button
                  size="small"
                  onClick={() => setSelectOpen(false)}
                  variant="contained"
                >
                  {t("okay")}
                </Button>
              </Box>
            </Select>
            {/* Error message if no exercise types selected */}
            {settings.selectedTypes.length === 0 && (
              <FormHelperText>
                {t("pleaseChooseAtleastOneExercise")}
              </FormHelperText>
            )}
          </FormControl>
        </Box>
      )}

      {/* Time and Frequency Configuration Grid */}
      <Grid
        container
        spacing={isMobile ? 1 : 2}
        sx={{
          mb: 3,
          maxWidth: "100%",
        }}
      >
        {/* Start Time Picker (HH:mm format) */}
        <Grid item xs={12} sm={4}>
          <TimePickerComponent
            label={t("startTime")}
            ampm={false}
            value={
              settings.startTime ? dayjs(settings.startTime, "HH:mm") : null
            }
            onChange={(newValue) => handleTimeChange("startTime", newValue)}
            disabled={disabled || !settings.isEnabled}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                InputProps: {
                  readOnly: true,
                },
              },
              ...(isMobile
                ? {}
                : {
                    textField: {
                      onFocus: (e) => {
                        e.target.readOnly = false;
                        e.target.blur();
                        e.target.readOnly = true;
                      },
                      onClick: (e) => {
                        e.target.readOnly = false;
                        e.target.blur();
                        e.target.focus();
                        e.target.readOnly = true;
                      },
                    },
                  }),
            }}
          />
        </Grid>

        {/* End Time Picker (HH:mm format) */}
        <Grid item xs={12} sm={4}>
          <TimePickerComponent
            label={t("endTime")}
            ampm={false}
            value={settings.endTime ? dayjs(settings.endTime, "HH:mm") : null}
            onChange={(newValue) => handleTimeChange("endTime", newValue)}
            disabled={disabled || !settings.isEnabled}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                InputProps: {
                  readOnly: true,
                },
              },
              ...(isMobile
                ? {}
                : {
                    textField: {
                      onFocus: (e) => {
                        e.target.readOnly = false;
                        e.target.blur();
                        e.target.readOnly = true;
                      },
                      onClick: (e) => {
                        e.target.readOnly = false;
                        e.target.blur();
                        e.target.focus();
                        e.target.readOnly = true;
                      },
                    },
                  }),
            }}
          />
        </Grid>

        {/* Reminder Frequency Dropdown (0.5h to 4h intervals) */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <Select
              value={settings.frequency}
              onChange={(e) => onUpdateSetting("frequency", e.target.value)}
              disabled={disabled || !settings.isEnabled}
              displayEmpty
            >
              {frequencyOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Days of Week Selection: Checkboxes for each day (Monday-Sunday) */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t("activeDays")}
        </Typography>
        <FormGroup>
          {/* Flex row of day checkboxes with responsive gap */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: isMobile ? 0.5 : 1,
              maxWidth: "100%",
            }}
          >
            {dayOptions.map((day) => (
              <FormControlLabel
                key={day.value}
                control={
                  <Checkbox
                    checked={settings.selectedDays.includes(day.value)}
                    onChange={() => onToggleDay(day.value)}
                    disabled={disabled || !settings.isEnabled}
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                    {day.label}
                  </Typography>
                }
                sx={{
                  mr: 1,
                  ml: 0,
                  "& .MuiTypography-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            ))}
          </Box>
        </FormGroup>
      </Box>

      {/* Physio Mode Section */}
      {/* For dedicated physio users: disabled checkbox showing physio mode is always on */}
      {isPhysioUser && currentProduct === "physio_user" && (
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={<Checkbox checked disabled sx={{ color: "#05b67f" }} />}
            label={
              <Typography
                variant="subtitle2"
                sx={{ color: "#05b67f", fontWeight: 600 }}
              >
                {t("Physio")}
              </Typography>
            }
          />
        </Box>
      )}

      {/* For corporate/preventive_course users: optional physio mode toggle */}
      {!isPhysioUser && isCurrentProductPhysioSelectable && (
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.isPhysioEnabled}
                onChange={(e) => handlePhysioToggle(e)}
                disabled={disabled || !settings.isEnabled}
                sx={{
                  color: "#05b67f",
                  "&.Mui-checked": { color: "#05b67f" },
                }}
              />
            }
            label={
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#05b67f",
                    fontWeight: 600,
                  }}
                >
                  {t("Physio")}
                </Typography>
                {/* Helper text explaining physio mode (physiotherapy exercises instead of regular exercises) */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {t("physioCheckbox")}
                </Typography>
              </Box>
            }
          />
        </Box>
      )}
      {/* Save Button (appears only when settings have been modified) */}
      {settings.isDirty && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={disabled || !settings.isDirty}
            sx={{
              minWidth: 120,
              fontWeight: 600,
            }}
          >
            {t("save")}
          </Button>
        </Box>
      )}

      {/* Settings Summary Card (displays when reminders enabled and exercises selected) */}
      {settings.isEnabled && settings.selectedTypes.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: "primary.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "primary.200",
          }}
        >
          {/* Summary text: frequency, time window, active days, exercise types, physio status */}
          <Typography variant="caption" color="primary.main">
            <strong>{t("summary")}:</strong> {t("exerciseRemindersEvery")}{" "}
            {frequencyOptions.find((f) => f.value === settings.frequency)
              ?.label || settings.frequency}{" "}
            {t("from")} {settings.startTime} {t("to")} {settings.endTime}
            {settings.selectedDays.length > 0 && (
              <span>
                {" "}
                {t("on")}{" "}
                {settings.selectedDays
                  .map(
                    (dayId) => dayOptions.find((d) => d.value === dayId)?.label
                  )
                  .join(", ")}
              </span>
            )}
            <br />
            <strong>{t("exerciseTypes")}:</strong> {getSelectedTypesDisplay()}
            {settings.isPhysioEnabled && (
              <span>
                {" "}
                • <strong>{t("physioIncluded")}</strong>
              </span>
            )}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

// Export the exercise settings form component
export default ExerciseSettingsForm;
